import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tabs, Tab, Avatar, Grid, Card, CardContent, Divider, MenuItem,
  Select, FormControl, InputLabel, LinearProgress, Snackbar, Alert, Tooltip
} from '@mui/material';
import {
  Search, Edit, Visibility, Add, Close, Download, TrendingUp,
  School, Person, EventAvailable, Grade, Refresh
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const mockStudents = [
  { id: 'STU-001', firstName: 'Emma', lastName: 'Johnson', grade: '5th Grade', gradeNum: 5, teacher: 'Mrs. Smith', gpa: 3.6, attendance: 97.9, status: 'enrolled', enrollDate: '2022-08-15', email: 'parent.johnson@email.com', phone: '(555) 201-3344' },
  { id: 'STU-002', firstName: 'Liam', lastName: 'Parker', grade: '6th Grade', gradeNum: 6, teacher: 'Mr. Davis', gpa: 3.2, attendance: 94.1, status: 'enrolled', enrollDate: '2021-08-20', email: 'jparker@email.com', phone: '(555) 312-4455' },
  { id: 'STU-003', firstName: 'Sophia', lastName: 'Martinez', grade: '7th Grade', gradeNum: 7, teacher: 'Mrs. Williams', gpa: 3.9, attendance: 99.0, status: 'enrolled', enrollDate: '2020-08-18', email: 'rosa.m@email.com', phone: '(555) 556-7788' },
  { id: 'STU-004', firstName: 'Noah', lastName: 'Thompson', grade: '5th Grade', gradeNum: 5, teacher: 'Mrs. Smith', gpa: 2.8, attendance: 88.5, status: 'probation', enrollDate: '2022-08-15', email: 'kthompson@email.com', phone: '(555) 667-8899' },
  { id: 'STU-005', firstName: 'Ava', lastName: 'Garcia', grade: '8th Grade', gradeNum: 8, teacher: 'Mr. Carter', gpa: 4.0, attendance: 100, status: 'enrolled', enrollDate: '2019-08-19', email: 'cgarcia@email.com', phone: '(555) 778-9900' },
  { id: 'STU-006', firstName: 'Mason', lastName: 'Wilson', grade: '3rd Grade', gradeNum: 3, teacher: 'Mrs. Green', gpa: 3.4, attendance: 96.2, status: 'enrolled', enrollDate: '2024-01-08', email: 'lwilson@email.com', phone: '(555) 889-0011' },
  { id: 'STU-007', firstName: 'Isabella', lastName: 'Anderson', grade: '6th Grade', gradeNum: 6, teacher: 'Mr. Davis', gpa: 3.7, attendance: 95.5, status: 'enrolled', enrollDate: '2021-08-20', email: 'randerson@email.com', phone: '(555) 990-1122' },
  { id: 'STU-008', firstName: 'James', lastName: 'Thomas', grade: '9th Grade', gradeNum: 9, teacher: 'Ms. Nguyen', gpa: 3.1, attendance: 91.3, status: 'enrolled', enrollDate: '2018-08-17', email: 'pthomas@email.com', phone: '(555) 101-2233' },
  { id: 'STU-009', firstName: 'Mia', lastName: 'Jackson', grade: '4th Grade', gradeNum: 4, teacher: 'Mr. Lee', gpa: 3.5, attendance: 97.1, status: 'enrolled', enrollDate: '2023-08-14', email: 'bjackson@email.com', phone: '(555) 212-3344' },
  { id: 'STU-010', firstName: 'Ethan', lastName: 'White', grade: '7th Grade', gradeNum: 7, teacher: 'Mrs. Williams', gpa: 2.5, attendance: 82.0, status: 'inactive', enrollDate: '2020-08-18', email: 'jwhite@email.com', phone: '(555) 323-4455' },
];

const gradeSubjects = ['Mathematics', 'English Language Arts', 'Science', 'History', 'Bible Studies', 'Physical Education', 'Art', 'Music'];

const statusConfig = {
  enrolled: { label: 'Enrolled', color: 'success' },
  probation: { label: 'Academic Probation', color: 'warning' },
  inactive: { label: 'Inactive', color: 'error' },
  graduated: { label: 'Graduated', color: 'info' },
};

const StudentsAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/enrollment')) return 1;
    if (location.pathname.includes('/records')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const filtered = mockStudents.filter(s => {
    const searchMatch = !search || `${s.firstName} ${s.lastName} ${s.id} ${s.teacher}`.toLowerCase().includes(search.toLowerCase());
    const gradeMatch = gradeFilter === 'all' || s.grade === gradeFilter;
    return searchMatch && gradeMatch;
  });

  const grades = [...new Set(mockStudents.map(s => s.grade))].sort((a, b) => {
    return parseInt(a) - parseInt(b);
  });

  const avgGpa = (mockStudents.reduce((sum, s) => sum + s.gpa, 0) / mockStudents.length).toFixed(2);
  const avgAttendance = (mockStudents.reduce((sum, s) => sum + s.attendance, 0) / mockStudents.length).toFixed(1);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Student Management</Typography>
          <Typography variant="body2" color="text.secondary">{mockStudents.length} enrolled students</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />}>Export</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialog(true)}>Add Student</Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Students', value: mockStudents.length, color: 'primary.main', icon: <School /> },
          { label: 'Enrolled', value: mockStudents.filter(s => s.status === 'enrolled').length, color: 'success.main', icon: <Person /> },
          { label: 'Avg GPA', value: avgGpa, color: 'info.main', icon: <Grade /> },
          { label: 'Avg Attendance', value: `${avgAttendance}%`, color: 'warning.main', icon: <EventAvailable /> },
        ].map((stat, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{ color: stat.color, mb: 0.5 }}>{stat.icon}</Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab label="All Students" />
            <Tab label="Enrollment" />
            <Tab label="Academic Records" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search students..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            size="small" sx={{ minWidth: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Grade</InputLabel>
            <Select value={gradeFilter} label="Grade" onChange={(e) => setGradeFilter(e.target.value)}>
              <MenuItem value="all">All Grades</MenuItem>
              {grades.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* All Students / Enrollment Tab */}
        {(tab === 0 || tab === 1) && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Homeroom Teacher</TableCell>
                  {tab === 0 && <TableCell sx={{ fontWeight: 600 }}>GPA</TableCell>}
                  {tab === 0 && <TableCell sx={{ fontWeight: 600 }}>Attendance</TableCell>}
                  {tab === 1 && <TableCell sx={{ fontWeight: 600 }}>Enroll Date</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.9rem' }}>
                          {s.firstName.charAt(0)}{s.lastName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.id}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{s.grade}</TableCell>
                    <TableCell>{s.teacher}</TableCell>
                    {tab === 0 && (
                      <TableCell>
                        <Chip label={s.gpa.toFixed(1)} color={s.gpa >= 3.5 ? 'success' : s.gpa >= 3.0 ? 'warning' : 'error'} size="small" />
                      </TableCell>
                    )}
                    {tab === 0 && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                          <LinearProgress variant="determinate" value={s.attendance} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} color={s.attendance >= 95 ? 'success' : s.attendance >= 90 ? 'warning' : 'error'} />
                          <Typography variant="caption">{s.attendance}%</Typography>
                        </Box>
                      </TableCell>
                    )}
                    {tab === 1 && <TableCell>{s.enrollDate}</TableCell>}
                    <TableCell>
                      <Chip label={statusConfig[s.status].label} color={statusConfig[s.status].color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Profile"><IconButton size="small" onClick={() => setSelected(s)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small"><Edit fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Academic Records Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            {selected ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Button startIcon={<Close />} onClick={() => setSelected(null)} size="small">Back to List</Button>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{selected.firstName} {selected.lastName} — Academic Record</Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={1} sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem', mx: 'auto', mb: 1 }}>
                            {selected.firstName.charAt(0)}{selected.lastName.charAt(0)}
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>{selected.firstName} {selected.lastName}</Typography>
                          <Typography variant="body2" color="text.secondary">{selected.id}</Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        {[
                          ['Grade', selected.grade],
                          ['Teacher', selected.teacher],
                          ['GPA', selected.gpa.toFixed(2)],
                          ['Attendance', `${selected.attendance}%`],
                          ['Enrolled', selected.enrollDate],
                          ['Status', statusConfig[selected.status].label],
                        ].map(([label, val]) => (
                          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{val}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card elevation={1} sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Current Term Grades</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {gradeSubjects.map((sub, i) => {
                              const pct = Math.round(70 + Math.random() * 30);
                              const letter = pct >= 93 ? 'A' : pct >= 90 ? 'A-' : pct >= 87 ? 'B+' : pct >= 83 ? 'B' : pct >= 80 ? 'B-' : 'C+';
                              const color = letter.startsWith('A') ? 'success' : letter.startsWith('B') ? 'warning' : 'error';
                              return (
                                <TableRow key={sub}>
                                  <TableCell>{sub}</TableCell>
                                  <TableCell><Chip label={letter} color={color} size="small" /></TableCell>
                                  <TableCell sx={{ minWidth: 140 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinearProgress variant="determinate" value={pct} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} color={color} />
                                      <Typography variant="caption">{pct}%</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>Select a student to view their academic record:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {filtered.map(s => (
                    <Button key={s.id} variant="outlined" onClick={() => setSelected(s)} sx={{ borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: '0.75rem' }}>
                          {s.firstName.charAt(0)}
                        </Avatar>
                        {s.firstName} {s.lastName}
                      </Box>
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Student Detail Dialog */}
      <Dialog open={!!selected && tab !== 2} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">{selected.firstName} {selected.lastName}</Typography>
                <Typography variant="caption" color="text.secondary">{selected.id}</Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small"><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {[
                  ['Grade Level', selected.grade],
                  ['Homeroom Teacher', selected.teacher],
                  ['GPA', selected.gpa.toFixed(2)],
                  ['Attendance Rate', `${selected.attendance}%`],
                  ['Enrollment Date', selected.enrollDate],
                  ['Status', statusConfig[selected.status].label],
                  ['Parent Email', selected.email],
                  ['Parent Phone', selected.phone],
                ].map(([label, val]) => (
                  <Grid item xs={6} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{val}</Typography>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
              <Button variant="outlined" startIcon={<Grade />} onClick={() => { setTab(2); }}>View Full Record</Button>
              <Button variant="contained" startIcon={<Edit />}>Edit Student</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add New Student
          <IconButton onClick={() => setAddDialog(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}><TextField label="First Name" fullWidth required /></Grid>
            <Grid item xs={6}><TextField label="Last Name" fullWidth required /></Grid>
            <Grid item xs={6}><TextField type="date" label="Date of Birth" fullWidth InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Grade Level</InputLabel>
                <Select label="Grade Level">
                  {['Kindergarten','1st Grade','2nd Grade','3rd Grade','4th Grade','5th Grade','6th Grade','7th Grade','8th Grade','9th Grade','10th Grade','11th Grade','12th Grade'].map(g => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField label="Parent / Guardian Name" fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Parent Email" type="email" fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Parent Phone" fullWidth /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setAddDialog(false); setSnackbar({ open: true, message: 'Student added successfully' }); }}>Add Student</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentsAdmin;
